import React, { FC, useState } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import WeduCheckbox from "../../../../components/WeduCheckbox/WeduCheckbox";
import { ReactComponent as OtherFees } from "../../../../assets/svg/other-fees.svg";
import { UserRole } from "../../../user/enums/UserRole";
import { useAuth } from "../../../auth/hooks/useAuth";
import { calcAccommodationFeesAndTotalSum } from "../../helpers/helperFunctions";
import { ProgramAccommodationFee } from "../../constants";
import { IFeeViewMoreFrontEndModel } from "../../types";
import { IconWithText } from "../IconWithText";
import { LanguageCourseAccommodationFee } from "../../../language-course/types";
import Masker from "../../../../components/Masker/Masker";
import { WrapperType } from "../../../../types";

const useStyles = makeStyles(() => ({
  feeIconWithDuration: {
    display: "flex",
  },
  duration: {
    paddingTop: 10,
    paddingLeft: 5,
    color: "#858997",
    fontSize: 16,
  },
  feesMainCont: {
    marginTop: 16,
    border: "1px solid #C5C5C5",
    borderRadius: 15,
  },
  feesSubCont: {
    padding: "8px 32px 0 32px",
  },
  feeSubCont: {
    borderBottom: "1px solid #C5C5C5",
    marginTop: 8,
    paddingBottom: 7,
  },
  feeNameAmountCont: {
    display: "flex",
    justifyContent: "space-between",
    borderBottom: "1px solid #C5C5C5",
    paddingBottom: 5,
    marginTop: 8,
  },
  feeMainName: {
    fontSize: 17,
    fontWeight: 500,
    color: "#957F2F",
  },
  feeAmount: {
    fontSize: 18,
    color: "#231F20",
  },
  subFeesCont: {
    paddingLeft: 64,
    marginRight: -14,
  },
  subFeesWithCheckboxCont: {
    paddingLeft: 35,
    marginRight: -14,
  },
  subFeeWithCheckboxCont: {
    display: "grid",
    gridTemplateColumns: "29px 1fr",
  },
  subFeeNameAmountCont: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 14px",
    "&:hover": {
      backgroundColor: "#EEEFF380",
      borderRadius: 15,
    },
  },
  accommodationFeeNameAmountCont: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 14px",
  },
  feeName: {
    fontSize: 17,
    color: "#858997",
  },
  tuitionWithScholarshipMainContainer: {
    padding: "13px 0",
  },
  tuitionWithScholarshipContainer: {
    backgroundColor: "#EEEFF3",
    padding: "16px 32px",
    height: 123,
  },
  tuitionWithScholarshipSubContainer: {
    display: "flex",
    justifyContent: "space-between",
    "& + &": {
      marginTop: 8,
    },
  },
  tuitionLabel: {
    fontSize: 18,
    color: "#231F20",
  },
  bold: {
    fontWeight: "bold",
  },
  tuitionWithoutScholarshipLabel: {
    marginRight: 24,
    fontSize: 17,
    color: "#858997",
    textDecorationLine: "line-through",
  },
}));

interface IFeesWidgetProps {
  fees: IFeeViewMoreFrontEndModel;
  isMasked?: boolean;
}

export const FeesWidget: FC<IFeesWidgetProps> = ({
  fees,
  isMasked = false,
}) => {
  const classes = useStyles();

  const formData = useFormState();
  const form = useForm();
  const [accommodationType, setAccommodationType] = useState("");
  const { user } = useAuth();
  const isStudent = user.role === UserRole.Student;
  const isParent = user.role === UserRole.Parent;

  if (
    (accommodationType === "" ||
      accommodationType === ProgramAccommodationFee.HOMESTAY) &&
    formData.values[ProgramAccommodationFee.ROOM_AND_BOARD]
  ) {
    form.change(ProgramAccommodationFee.HOMESTAY, false);
    setAccommodationType(ProgramAccommodationFee.ROOM_AND_BOARD);
  }

  if (
    (accommodationType === "" ||
      accommodationType === LanguageCourseAccommodationFee.HOMESTAY) &&
    formData.values[LanguageCourseAccommodationFee.RESIDENCE]
  ) {
    form.change(LanguageCourseAccommodationFee.HOMESTAY, false);
    setAccommodationType(LanguageCourseAccommodationFee.RESIDENCE);
  }

  if (
    (accommodationType === "" ||
      accommodationType === ProgramAccommodationFee.ROOM_AND_BOARD) &&
    formData.values[ProgramAccommodationFee.HOMESTAY]
  ) {
    form.change(ProgramAccommodationFee.ROOM_AND_BOARD, false);
    setAccommodationType(ProgramAccommodationFee.HOMESTAY);
  }

  if (
    (accommodationType === "" ||
      accommodationType === LanguageCourseAccommodationFee.RESIDENCE) &&
    formData.values[LanguageCourseAccommodationFee.HOMESTAY]
  ) {
    form.change(LanguageCourseAccommodationFee.RESIDENCE, false);
    setAccommodationType(LanguageCourseAccommodationFee.HOMESTAY);
  }

  const { calcTotalWithCurrency, calcTotalWithSupportWithCurrency } =
    calcAccommodationFeesAndTotalSum(
      fees.accommodationFees.fees,
      fees.total.total,
      fees.total.totalWithSupport,
      fees.currencyId,
      formData.values,
    );

  return (
    <div>
      <div className={classes.feeIconWithDuration}>
        <IconWithText Icon={OtherFees} title="Fees" />
        <div className={classes.duration}>{` / ${fees.duration}`}</div>
      </div>
      <div className={classes.feesMainCont}>
        <div className={classes.feesSubCont}>
          <div className={classes.feeNameAmountCont}>
            <span className={classes.feeMainName}>{fees.mainFee.name}</span>
            <Masker
              wrapperType={WrapperType.Span}
              className={classes.feeAmount}
              isMasked={isMasked}
              children={fees.mainFee.fee}
            />
          </div>
          {!isStudent && !isParent && (
            <div className={classes.feeNameAmountCont}>
              <span className={classes.feeMainName}>Commission Fee</span>
              <Masker
                wrapperType={WrapperType.Span}
                className={classes.feeAmount}
                isMasked={isMasked}
                children={fees.commission}
              />
            </div>
          )}
          {fees.accommodationFees.hasFees &&
            (!!fees.accommodationFees.fees.length ? (
              <div className={classes.feeSubCont}>
                <div className={classes.feeMainName}>Accommodation Fees</div>
                <div className={classes.subFeesWithCheckboxCont}>
                  {fees.accommodationFees.fees.map((accommodationFee) => (
                    <div
                      key={accommodationFee.name}
                      className={classes.subFeeWithCheckboxCont}
                    >
                      <Field
                        type="checkbox"
                        name={accommodationFee.name}
                        component={WeduCheckbox}
                      />
                      <div className={classes.subFeeNameAmountCont}>
                        <span className={classes.feeName}>
                          {accommodationFee.label}
                        </span>
                        <Masker
                          wrapperType={WrapperType.Span}
                          className={classes.feeAmount}
                          isMasked={isMasked}
                          children={accommodationFee.amountWithCurrency}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={classes.feeNameAmountCont}>
                <span className={classes.feeMainName}>Accommodation Fees</span>
                <Masker
                  wrapperType={WrapperType.Span}
                  className={classes.feeAmount}
                  isMasked={isMasked}
                  children={fees.accommodationFees.zeroFee}
                />
              </div>
            ))}
          {!!fees.otherFees.fees.length ? (
            <div className={classes.feeSubCont}>
              <div className={classes.feeMainName}>Other Fees</div>
              <div className={classes.subFeesCont}>
                {fees.otherFees.fees.map((fee) => (
                  <div key={fee.name} className={classes.subFeeNameAmountCont}>
                    <Masker
                      wrapperType={WrapperType.Span}
                      className={classes.feeName}
                      isMasked={isMasked}
                      children={fee.name}
                    />
                    <Masker
                      wrapperType={WrapperType.Span}
                      className={classes.feeAmount}
                      isMasked={isMasked}
                      children={fee.amount}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={classes.feeNameAmountCont}>
              <span className={classes.feeMainName}>Other Fees</span>
              <Masker
                wrapperType={WrapperType.Span}
                className={classes.feeAmount}
                isMasked={isMasked}
                children={fees.otherFees.otherFeesTotal}
              />
            </div>
          )}
          {!!fees.supports?.length &&
            fees.supports.map((support) => (
              <div key={support.name} className={classes.feeSubCont}>
                <div className={classes.feeMainName}>{support.name}</div>
                <div className={classes.subFeesCont}>
                  {support.support.map((fee) => (
                    <div
                      key={fee.name}
                      className={classes.subFeeNameAmountCont}
                    >
                      <span className={classes.feeName}>{fee.name}</span>
                      <Masker
                        wrapperType={WrapperType.Span}
                        className={classes.feeAmount}
                        isMasked={isMasked}
                        children={fee.amount}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
        <div className={classes.tuitionWithScholarshipMainContainer}>
          <div className={classes.tuitionWithScholarshipContainer}>
            <div className={classes.tuitionWithScholarshipSubContainer}>
              <span className={classes.tuitionLabel}>Other Fees</span>
              <div>
                {fees.otherFees.hasSupport && (
                  <Masker
                    wrapperType={WrapperType.Span}
                    className={classes.tuitionWithoutScholarshipLabel}
                    isMasked={isMasked}
                    children={fees.otherFees.otherFeesTotal}
                  />
                )}
                <Masker
                  wrapperType={WrapperType.Span}
                  className={classes.tuitionLabel}
                  isMasked={isMasked}
                  children={fees.otherFees.otherFeesTotalWithSupport}
                />
              </div>
            </div>
            <div className={classes.tuitionWithScholarshipSubContainer}>
              <span className={classes.tuitionLabel}>{fees.mainFee.name}</span>
              <div>
                {fees.mainFee.hasSupport && (
                  <Masker
                    wrapperType={WrapperType.Span}
                    className={classes.tuitionWithoutScholarshipLabel}
                    isMasked={isMasked}
                    children={fees.mainFee.fee}
                  />
                )}
                <Masker
                  wrapperType={WrapperType.Span}
                  className={classes.tuitionLabel}
                  isMasked={isMasked}
                  children={fees.mainFee.feeWithSupport}
                />
              </div>
            </div>
            <div className={classes.tuitionWithScholarshipSubContainer}>
              <span className={clsx(classes.tuitionLabel, classes.bold)}>
                Total Amount
              </span>
              <div>
                {fees.total.hasSupport && (
                  <Masker
                    wrapperType={WrapperType.Span}
                    className={classes.tuitionWithoutScholarshipLabel}
                    isMasked={isMasked}
                    children={calcTotalWithCurrency}
                  />
                )}
                <Masker
                  wrapperType={WrapperType.Span}
                  className={clsx(classes.tuitionLabel, classes.bold)}
                  isMasked={isMasked}
                  children={calcTotalWithSupportWithCurrency}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
